import { useState, useCallback, useEffect, useRef } from 'react';
import { searchClient } from 'components/essentials/AlgoliaSearchClient';

/**
 * Custom hook for managing customer photos loading from Algolia
 * @param {Object} filterState - Current filter state
 * @param {Object} filterActions - Filter action functions
 * @param {Function} onPhotoFound - Callback when photos are loaded
 * @returns {Object} Customer photos loading state and functions
 */
const useCustomerPhotosLoader = (filterState, filterActions, onPhotoFound) => {
  const [photos, setPhotos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  // Use ref to store the function to avoid re-render issues
  const loadPhotosFromAlgoliaRef = useRef();

  const resetPagination = useCallback(() => {
    setPhotos([]);
    setCurrentPage(1);
    setHasMore(true);
  }, []);

  const loadPhotosFromAlgolia = useCallback(
    async page => {
      if (loading) return;

      setLoading(true);

      try {
        const facetFiltersString = filterState
          ? filterActions.buildFacetFilters()
          : '';
        // For searchMaterial URL parameters, get the query directly from URL
        const urlParams = new URLSearchParams(window.location.search);
        const searchMaterialFromUrl = urlParams.get('materialOption');
        const searchQuery =
          searchMaterialFromUrl || filterState?.searchQuery || '';
        let facetFiltersArray = [];
        if (facetFiltersString) {
          try {
            facetFiltersArray = JSON.parse(facetFiltersString);
          } catch (e) {
            throw new Error('Failed to parse facet filters');
          }
        }

        const response = await searchClient.search([
          {
            indexName: 'customer_photos',
            params: {
              query: searchQuery,
              hitsPerPage: 24,
              page: page - 1,
              facetFilters:
                facetFiltersArray.length > 0 ? facetFiltersArray : undefined,
              facets: ['*'],
            },
          },
        ]);

        const newPhotos = response.results[0].hits.map(hit => ({
          photo_id: hit.photo_id || hit.objectID,
          image_link: hit.image_link || hit.photo_image || hit.photo_url,
          customer_first_name:
            hit.customer_first_name || hit.photo_full_name || 'Customer',
          photo_comment: hit.photo_comment || hit.photo_description || '',
          photo_full_name:
            hit.photo_full_name || hit.customer_first_name || 'Customer',
          products: hit.products || [],
        }));

        if (page === 1) {
          setPhotos(newPhotos);
        } else {
          setPhotos(prev => [...prev, ...newPhotos]);
        }

        setCurrentPage(page);
        const totalHits = response.results[0].nbHits;
        const totalPages = Math.ceil(totalHits / 24);
        setHasMore(page < totalPages);

        if (onPhotoFound && page === 1) {
          onPhotoFound(newPhotos);
        }
      } catch (error) {
        throw new Error('Failed to load photos from Algolia');
      } finally {
        setLoading(false);
        setFilterLoading(false);
      }
    },
    [filterState, filterActions, loading, onPhotoFound]
  );

  // Store the function in ref to make it stable for ESLint
  loadPhotosFromAlgoliaRef.current = loadPhotosFromAlgolia;

  useEffect(() => {
    if (filterState?.isInitialized && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlFilters =
        urlParams.get('material') ||
        urlParams.get('materialOption') ||
        urlParams.get('room') ||
        urlParams.get('color') ||
        urlParams.get('pid');

      if (hasUrlFilters) {
        // Small delay to ensure filter state is updated from URL parameters
        const timeoutId = setTimeout(() => {
          setFilterLoading(true);
          resetPagination();
          loadPhotosFromAlgoliaRef.current(1);
        }, 50);

        return () => clearTimeout(timeoutId);
      }
      setFilterLoading(true);
      resetPagination();
      loadPhotosFromAlgoliaRef.current(1);
      return undefined;
    }
    return undefined;
  }, [
    filterState,
    filterState?.isInitialized,
    filterState?.materialFilter,
    filterState?.roomFilter,
    filterState?.colorFilters,
    filterState?.searchQuery,
    resetPagination,
    loadPhotosFromAlgoliaRef,
  ]);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadPhotosFromAlgolia(currentPage + 1);
    }
  }, [loading, hasMore, currentPage, loadPhotosFromAlgolia]);

  return {
    photos,
    currentPage,
    hasMore,
    loading,
    filterLoading,
    loadPhotosFromAlgolia,
    handleLoadMore,
    resetPagination,
  };
};

export default useCustomerPhotosLoader;
